import { bucketKey, formatBucket, granularityForSpan } from '@lib/charts/timeBuckets';
import { useLocalization } from '@lib/localization';
import { Box, Paper } from '@mui/material';
import { type BarSeriesType } from '@mui/x-charts';
import { BarChart } from '@mui/x-charts/BarChart';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { useMemo } from 'react';

export interface TrendDataPoint {
  date: Date;
  total: number;
  ok: number;
  notChecked: number;
  warning: number;
  error: number;
}

interface InspectionTrendChartProps {
  data: TrendDataPoint[];
  order?: 'asc' | 'desc';
}

const InspectionTrendChart: React.FC<InspectionTrendChartProps> = ({ data, order = 'asc' }) => {
  const { t, currentLanguage } = useLocalization();
  const noticeColors = useNoticeColors();

  const labelColor = 'rgba(255,255,255,0.7)';

  // Bars need a band axis, so each point carries the label of the bucket it lands in.
  const dataset = useMemo(() => {
    const dates = data.map((d) => new Date(d.date));
    const granularity = granularityForSpan(dates);
    const buckets = new Map<string, { label: string, ok: number, warning: number, error: number, notChecked: number }>();

    data.forEach((point) => {
      const key = bucketKey(new Date(point.date), granularity);
      const bucket = buckets.get(key)
        ?? { label: formatBucket(key, granularity, currentLanguage), ok: 0, warning: 0, error: 0, notChecked: 0 };

      bucket.ok += point.ok;
      bucket.warning += point.warning;
      bucket.error += point.error;
      bucket.notChecked += point.notChecked;

      buckets.set(key, bucket);
    });

    return [...buckets.entries()]
      .sort(([a], [b]) => (order === 'asc' ? a.localeCompare(b) : b.localeCompare(a)))
      .map(([, bucket]) => bucket);
  }, [data, order, currentLanguage]);

  return (
    <Paper
      sx={{
        p: 3,
        pl: 0,
        height: 400,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1 }}>
        <BarChart
          height={360}
          dataset={dataset}
          yAxis={[
            {
              tickMinStep: 1
            },
          ]}
          xAxis={[
            {
              dataKey: 'label',
              scaleType: 'band',
            },
          ]}
          series={
            [
              {
                dataKey: 'ok',
                label: t('Common_Successful'),
                color: noticeColors.Ok,
              },
              {
                dataKey: 'warning',
                label: t('Common_Warning'),
                color: noticeColors.Warning,
              },
              {
                dataKey: 'error',
                label: t('Common_Error'),
                color: noticeColors.Error,
              },
              {
                dataKey: 'notChecked',
                label: t('Common_NotChecked'),
                color: noticeColors.NotChecked,
              },
            ] as BarSeriesType[]
          }
          borderRadius={4}
          grid={{ horizontal: true, vertical: false }}
          margin={{ left: 0, right: 20, top: 20, bottom: 40 }}
          slotProps={{
            legend: {
              direction: 'horizontal',
              position: { vertical: 'top', horizontal: 'end' },
            },
            tooltip: {
              sx: {
                '*': {
                  color: '#fff !important',
                },
              },
            },
            noDataOverlay: { message: t('Project_Overview_NoData') },
          }}
          sx={{
            '--Charts-axis-label-color': labelColor,
          }}
        />
      </Box>
    </Paper>
  );
};

export default InspectionTrendChart;
